import React, { useState, useEffect, useCallback } from 'react';
import { Layout, theme, Typography } from 'antd';
import Sidebar from './sidebar';
import MainHeader from './header';
import { IDLE_LOGOUT_MINUTES } from '../constants';

const { Content } = Layout;
const { Text } = Typography;

const STORAGE_KEY = 'tandemly_tab_hidden_at';

function useHiddenTabLogout(minutes) {
    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('name');
        localStorage.removeItem('role');
        localStorage.removeItem('courtID');
        localStorage.removeItem(STORAGE_KEY);
        window.location.href = '/login';
    }, []);

    const checkAndMaybeLogout = useCallback(() => {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const hiddenAt = parseInt(raw, 10);
        if (Number.isNaN(hiddenAt)) return;
        const limitMs = Math.max(1, minutes) * 60 * 1000;
        if (Date.now() - hiddenAt >= limitMs) {
            logout();
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, [minutes, logout]);

    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                localStorage.setItem(STORAGE_KEY, String(Date.now()));
            } else {
                checkAndMaybeLogout();
            }
        };

        const handlePageHide = () => {
            localStorage.setItem(STORAGE_KEY, String(Date.now()));
        };

        checkAndMaybeLogout();
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [checkAndMaybeLogout]);
}

export default function MainLayout({ children }) {
    const { token: { borderRadiusLG } } = theme.useToken();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [contentMargin, setContentMargin] = useState('var(--spacing-xl)');
    const [contentPadding, setContentPadding] = useState('var(--spacing-2xl)');

    useHiddenTabLogout(IDLE_LOGOUT_MINUTES);

    // Listen for sidebar collapse state changes
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    }, []);

    // Calculate responsive margins and padding
    useEffect(() => {
        const updateResponsiveSpacing = () => {
            const width = window.innerWidth;
            if (width < 768) {
                // Mobile
                setContentMargin('var(--spacing-md)');
                setContentPadding('var(--spacing-lg)');
            } else if (width < 1024) {
                // Tablet
                setContentMargin('var(--spacing-lg)');
                setContentPadding('var(--spacing-xl)');
            } else {
                // Desktop
                setContentMargin('var(--spacing-xl)');
                setContentPadding('var(--spacing-2xl)');
            }
        };

        updateResponsiveSpacing();
        window.addEventListener('resize', updateResponsiveSpacing);
        return () => window.removeEventListener('resize', updateResponsiveSpacing);
    }, []);

    const sidebarWidth = sidebarCollapsed ? 80 : 240;

    return (
        <Layout hasSider>
            <Sidebar />

            <div
                style={{
                    marginLeft: sidebarWidth,
                    background: 'var(--dark-bg-start)',
                    minHeight: '100vh',
                    width: '-webkit-fill-available',
                    transition: 'margin-left 0.3s ease-in-out'
                }}
            >
                <Layout style={{
                    background: 'transparent'
                }}>
                    <MainHeader />

                    <Content style={{
                        margin: contentMargin,
                        margin: '20px',
                        overflow: 'initial'
                    }}
                    className="main-content"
                >
                    <div
                        style={{
                            padding: contentPadding,
                            background: 'var(--gradient-card)',
                            border: '1px solid var(--border-light)',
                            borderRadius: 'var(--radius-2xl)',
                            boxShadow: 'var(--shadow-card)',
                            backdropFilter: 'blur(20px)',
                        }}
                    >
                        {children}
                    </div>

                    <footer style={{
                        textAlign: 'center',
                        padding: '16px 20px',
                        marginTop: 20,
                        marginBottom: 0,
                        color: 'var(--text-gray-dark)',
                        fontSize: '0.75rem',
                        borderTop: '1px solid var(--border-light)',
                    }}>
                        <Text style={{ color: 'var(--text-gray-dark)', fontSize: '0.75rem' }}>
                            © {new Date().getFullYear()} Tandemly. All rights reserved.
                        </Text>
                    </footer>
                </Content>
                </Layout>
            </div>
        </Layout>
    );
};